import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import cn from "classnames";
import style from "./style.module.scss";
import Expeditor from "../../../services/Expeditor";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { requestErrorDisplay } from "../../../utils";
import ExportOrders from "../section-item/components/ExportOrders";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { EXPEDITOR_TABS, EXPEDITOR_ALL_STATUSES } from "../../../mock";
import FilterIcon from "../../shared/icons/FilterIcon";
import { XIcon } from "lucide-react";
import { TRANSPORTATION_TYPES } from "../../../pages/orders/utils/railwayOrder";

const ExpeditorOrdersTab = ({
  transportationType = TRANSPORTATION_TYPES.ROAD,
  setStatus,
  changeSortColumn,
  sortColumn = null,
  onDownloadExcel,
  isCallTaps = false,
  // filter values (from useOrderFilter)
  countryFrom,
  countryTo,
  cityFrom,
  cityTo,
  loadingDate,
  creationDate,
  selectedStatus,
  selectedCurrency,
  senderName,
  receiverName,
  orderNo,
  executor,
  expeditorInsuranceIncluded = null,
  statusActive,
  activeTab,
  isFilterIcon = false,
  onOpenFilter = () => {},
  isFilterOpen = false,
  onReload,
  onReset,
}) => {
  const [orderCounts, setOrderCounts] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState(() => {
    const defaultTab = EXPEDITOR_TABS((t) => t).find((tab) => tab.key === "all");
    return defaultTab || EXPEDITOR_TABS((t) => t)[0];
  });
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownContent = useRef(null);
  const { t } = useTranslation();

  useClickOutside(dropdownContent, () => setDropdownOpen(false));

  const items = useMemo(() => {
    return EXPEDITOR_TABS(t).map((tab) => ({
      key: tab.key,
      statuses: tab.statuses,
      title: tab.title,
      quantity: orderCounts[tab.key] || 0,
    }));
  }, [orderCounts, t]);

  const activeStatusItem = items.find((item) => item.key === activeStatus?.key);

  const updateActiveStatus = (item) => {
    setActiveStatus(item);
    setStatus(item);
  };

  useEffect(() => {
    if (statusActive) {
      setActiveStatus((prev) => (prev?.key === statusActive.key ? prev : statusActive));
    }
  }, [statusActive?.key]);

  const buildCountParams = useCallback(() => {
    const statusGroups = EXPEDITOR_TABS(t).map((tab) => ({
      key: tab.key,
      status: tab.statuses,
    }));

    const statuses =
      statusActive?.key === "all"
        ? Array.isArray(selectedStatus) && selectedStatus.length > 0
          ? selectedStatus.map((s) => s.value)
          : EXPEDITOR_ALL_STATUSES
        : statusActive?.statuses || [];

    return {
      transportationType,
      statusGroups,
      statuses,
      countryIdFrom: countryFrom?.id || "",
      cityIdFrom: cityFrom?.id || "",
      countryIdTo: countryTo?.id || "",
      cityIdTo: cityTo?.id || "",
      startShippingDate:
        Array.isArray(loadingDate) && loadingDate[0]
          ? moment(loadingDate[0]).format("YYYY-MM-DD")
          : "",
      endShippingDate:
        Array.isArray(loadingDate) && loadingDate[1]
          ? moment(loadingDate[1]).format("YYYY-MM-DD")
          : "",
      startCreatedAt:
        Array.isArray(creationDate) && creationDate[0]
          ? moment(creationDate[0]).format("YYYY-MM-DD")
          : "",
      endCreatedAt:
        Array.isArray(creationDate) && creationDate[1]
          ? moment(creationDate[1]).format("YYYY-MM-DD")
          : "",
      currencies: selectedCurrency ? [selectedCurrency.value] : [],
      orderNo: String(orderNo || ""),
      senderName: senderName || "",
      recipientName: receiverName || "",
      senderCompanyName: executor ? executor.companyName : "",
      expeditorInsuranceIncluded,
    };
  }, [
    transportationType,
    t,
    statusActive,
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    receiverName,
    orderNo,
    executor,
    expeditorInsuranceIncluded,
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildCountParams();
      const response = await Expeditor.getStatusWithOrderCountsForExpeditor(activeTab, params);
      if (response?.data) {
        const counts = response.data.reduce((acc, item) => {
          acc[item.key] = item.count;
          return acc;
        }, {});
        setOrderCounts(counts);

        if (!statusActive) {
          const defaultTab = EXPEDITOR_TABS(t).find((tab) => tab.key === "all");
          if (defaultTab) {
            updateActiveStatus(defaultTab);
          }
        }
      }
    } catch (e) {
      requestErrorDisplay(e);
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildCountParams, statusActive, t]);

  useEffect(() => {
    fetchData();
  }, [
    transportationType,
    isCallTaps,
    activeTab,
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    receiverName,
    orderNo,
    executor,
    expeditorInsuranceIncluded,
  ]);

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      countryFrom ||
      countryTo ||
      cityFrom ||
      cityTo ||
      (Array.isArray(loadingDate) && loadingDate.length > 0 && loadingDate[0]) ||
      (Array.isArray(creationDate) && creationDate.length > 0 && creationDate[0]) ||
      (Array.isArray(selectedStatus) && selectedStatus.length > 0 && selectedStatus[0]) ||
      selectedCurrency ||
      senderName ||
      receiverName ||
      orderNo ||
      executor ||
      expeditorInsuranceIncluded !== null
    );
  }, [
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    receiverName,
    orderNo,
    executor,
    expeditorInsuranceIncluded,
  ]);

  return (
    <div className={cn(style.wrapper)} style={{ margin: "0", marginBottom: "16px", padding: "0" }}>
      {/* Dropdown для мобильной версии */}
      <div ref={dropdownContent} className={style.dropdownWrapper}>
        <div className={style.flex}>
          <button
            type="button"
            className={cn(style.button, "d-flex align-items-center", {
              [style.button_active]: activeStatus?.key,
            })}
            onClick={toggleDropdown}
          >
            {activeStatusItem?.title}
            <span className={cn(style.count)}>
              {activeStatusItem?.quantity || 0}
            </span>
            <div className={style.imgSelect}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
                className={style.checkmark}
              >
                <path
                  d="M1 1.5L5 5.5L9 1.5"
                  stroke="#909195"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
        {isDropdownOpen && (
          <div className={style.dropdownContent}>
            {items.map((item) => (
              <button
                type="button"
                key={"dropdown-" + item.key}
                className={cn(style.button, "d-flex align-items-center", {
                  [style.button_none]: item.key === activeStatus?.key,
                })}
                onClick={() => {
                  toggleDropdown();
                  updateActiveStatus(item);
                }}
              >
                {item.title}
                <span className={style.count}>{item.quantity}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop tabs */}
      <div className={style.mobile}>
        {items.map((item) => (
          <button
            type="button"
            key={"desktop-" + item.key}
            className={cn(style.button, "d-flex align-items-center text-nowrap", {
              [style.button_active]: item.key === activeStatus?.key,
            })}
            onClick={() => updateActiveStatus(item)}
          >
            {item.title}
            <span
              className={cn(style.count, {
                [style.count_active]: item.key === activeStatus?.key,
              })}
            >
              {item.quantity}
            </span>
          </button>
        ))}
      </div>

      <div className={cn(style.filterBtn, { [style.filterBtn_active]: isFilterOpen })}>
        <button
          type="button"
          className={style.innerBtn}
          onClick={onOpenFilter}
          title={t("orderFilter.title")}
        >
          <FilterIcon />
          {t("orderFilter.title")}
        </button>

        {onReset && hasActiveFilters && (
          <button
            type="button"
            className={cn(style.innerBtn, style.resetBtn)}
            onClick={onReset}
            title={t("orderFilter.resetButton")}
          >
            <XIcon size={20} />
          </button>
        )}
      </div>

      <ExportOrders onDownloadExcel={onDownloadExcel} />
    </div>
  );
};

export default ExpeditorOrdersTab;
